'use client';

import {
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { IcebergRestCatalogAuthType } from '@repo/api-gw-sdk';

import { FormTextField } from '@/components/form/formTextField';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';

import { CATALOG_NAME_RE, CatalogNameSection } from './CatalogNameSection';

import type { AddCatalogWizardState } from '../addCatalogWizardState';

const SECRETS_MANAGER_ARN_RE =
  /^arn:aws:secretsmanager:[a-z0-9-]+:\d{12}:secret:.+$/;

const AUTH_TYPE_OPTIONS = [
  {
    value: IcebergRestCatalogAuthType.ICEBERG_REST_CATALOG_AUTH_TYPE_NONE,
    label: 'None',
  },
  {
    value: IcebergRestCatalogAuthType.ICEBERG_REST_CATALOG_AUTH_TYPE_BASIC_AUTH,
    label: 'Basic Auth',
  },
  {
    value:
      IcebergRestCatalogAuthType.ICEBERG_REST_CATALOG_AUTH_TYPE_OAUTH2_BEARER_TOKEN,
    label: 'OAuth2 Bearer Token',
  },
  {
    value:
      IcebergRestCatalogAuthType.ICEBERG_REST_CATALOG_AUTH_TYPE_OAUTH2_CLIENT_CREDENTIALS,
    label: 'OAuth2 Client Credentials',
  },
];

export const CatalogIcebergConfigStep = (
  props: StepProps<AddCatalogWizardState>
) => {
  const {
    catalogName,
    icebergUri,
    icebergWarehouse,
    icebergAuthType,
    icebergCredentialsSecretArn,
  } = props.wizardState;

  const needsSecret =
    icebergAuthType !==
    IcebergRestCatalogAuthType.ICEBERG_REST_CATALOG_AUTH_TYPE_NONE;
  const isValidArn =
    !needsSecret || SECRETS_MANAGER_ARN_RE.test(icebergCredentialsSecretArn);
  const canGoNext =
    CATALOG_NAME_RE.test(catalogName) &&
    icebergUri.trim() !== '' &&
    icebergWarehouse.trim() !== '' &&
    isValidArn;

  return (
    <StepContainer
      stepperIndex={2}
      stepperLabels={props.stepperLabels}
      canGoNext={() => canGoNext}
      nextButtonText='Next'
      backButtonText='Back'
      onBackClick={props.back}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
    >
      <Stack gap='32px' padding='24px 40px'>
        <CatalogNameSection
          value={catalogName}
          onChange={(value) =>
            props.setWizardState({
              ...props.wizardState,
              catalogName: value,
            })
          }
        />

        <Stack gap='16px'>
          <Typography variant='subtitle2' fontWeight={700}>
            Iceberg REST Catalog Configuration
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Configure the Iceberg REST Catalog connection settings.
          </Typography>
        </Stack>

        <Stack gap='24px' maxWidth='540px'>
          <FormTextField
            title='REST Catalog URI'
            size='small'
            placeholder='e.g., https://catalog.example.com'
            value={icebergUri}
            onChange={(e) =>
              props.setWizardState({
                ...props.wizardState,
                icebergUri: e.target.value,
              })
            }
          />

          <FormTextField
            title='Warehouse'
            size='small'
            placeholder='e.g., my_warehouse'
            value={icebergWarehouse}
            onChange={(e) =>
              props.setWizardState({
                ...props.wizardState,
                icebergWarehouse: e.target.value,
              })
            }
          />

          <Stack gap='8px'>
            <Typography variant='subtitle1'>Authentication Type</Typography>
            <FormControl size='small'>
              <Select
                value={icebergAuthType}
                onChange={(e) => {
                  const authType = AUTH_TYPE_OPTIONS.find(
                    (o) => o.value === e.target.value
                  );
                  if (!authType) return;
                  props.setWizardState({
                    ...props.wizardState,
                    icebergAuthType: authType.value,
                    icebergCredentialsSecretArn:
                      authType.value ===
                      IcebergRestCatalogAuthType.ICEBERG_REST_CATALOG_AUTH_TYPE_NONE
                        ? ''
                        : props.wizardState.icebergCredentialsSecretArn,
                  });
                }}
              >
                {AUTH_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {needsSecret && (
            <FormTextField
              title='Credentials Secret ARN'
              size='small'
              placeholder='arn:aws:secretsmanager:us-east-1:123456789012:secret:my-secret'
              value={icebergCredentialsSecretArn}
              validation={{
                validateInput: (v) =>
                  v.trim() === '' || SECRETS_MANAGER_ARN_RE.test(v),
                errorMessage: () =>
                  'Must be a valid AWS Secrets Manager ARN (arn:aws:secretsmanager:<region>:<account-id>:secret:<name>)',
              }}
              onChange={(e) =>
                props.setWizardState({
                  ...props.wizardState,
                  icebergCredentialsSecretArn: e.target.value,
                })
              }
            />
          )}
        </Stack>
      </Stack>
    </StepContainer>
  );
};
